import * as THREE from "three";

/**
 * TS port of unity/Assets/RubberTireJam/Scripts/BandShape.cs
 *
 * Computes the outline of a rubber band wrapped around a set of pin
 * positions. Pins are treated as small circles; the band is the closed
 * convex curve formed by external tangents between consecutive pins joined
 * by outer arcs at every pin.
 *
 * Pins must already be in their "wrap order" (CW or CCW around the convex
 * hull). Winding is auto-detected via signed area.
 */

export interface Vec2Like {
  x: number;
  y: number;
}

/**
 * Fill `output` with the closed-loop polyline of a band wrapped around
 * `pins` at distance `offset` from each pin center, with `arcSegmentsPerCorner`
 * segments per outer arc.
 *
 * The returned polyline is *open* in the sense that the first and last
 * vertex are NOT duplicated — callers should close it themselves (e.g.
 * with THREE.LineLoop, or by repeating the first vertex).
 */
export function fillLoop(
  pins: Vec2Like[],
  offset: number,
  arcSegmentsPerCorner: number,
  output: THREE.Vector3[],
): void {
  output.length = 0;
  const n = pins.length;
  if (n === 0) return;

  if (n === 1) {
    const center = pins[0];
    const segs = Math.max(arcSegmentsPerCorner * 3, 12);
    for (let s = 0; s < segs; s++) {
      const angle = (s / segs) * Math.PI * 2;
      output.push(
        new THREE.Vector3(
          center.x + Math.cos(angle) * offset,
          center.y + Math.sin(angle) * offset,
          0,
        ),
      );
    }
    return;
  }

  if (n === 2) {
    const a = pins[0];
    const b = pins[1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.max(1e-6, Math.hypot(dx, dy));
    const dirX = dx / len;
    const dirY = dy / len;
    // perp-left
    const pLx = -dirY;
    const pLy = dirX;

    const half = Math.max(arcSegmentsPerCorner, 6);
    // Arc around a from +perpL → -perpL (going AWAY from b)
    for (let s = 0; s <= half; s++) {
      const t = s / half;
      const angle = Math.atan2(pLy, pLx) + t * Math.PI;
      output.push(
        new THREE.Vector3(a.x + Math.cos(angle) * offset, a.y + Math.sin(angle) * offset, 0),
      );
    }
    // Arc around b
    for (let s = 0; s <= half; s++) {
      const t = s / half;
      const angle = Math.atan2(-pLy, -pLx) + t * Math.PI;
      output.push(
        new THREE.Vector3(b.x + Math.cos(angle) * offset, b.y + Math.sin(angle) * offset, 0),
      );
    }
    return;
  }

  // Detect winding via signed area (shoelace). > 0 => CCW (y-up convention).
  let signedArea = 0;
  for (let i = 0; i < n; i++) {
    const ai = pins[i];
    const bi = pins[(i + 1) % n];
    signedArea += ai.x * bi.y - bi.x * ai.y;
  }
  const ccw = signedArea > 0;

  for (let i = 0; i < n; i++) {
    const prev = pins[(i - 1 + n) % n];
    const here = pins[i];
    const next = pins[(i + 1) % n];

    const inX = here.x - prev.x;
    const inY = here.y - prev.y;
    const inLen = Math.max(1e-6, Math.hypot(inX, inY));
    const dirInX = inX / inLen;
    const dirInY = inY / inLen;

    const outX = next.x - here.x;
    const outY = next.y - here.y;
    const outLen = Math.max(1e-6, Math.hypot(outX, outY));
    const dirOutX = outX / outLen;
    const dirOutY = outY / outLen;

    // Outer perpendicular: RIGHT of forward direction for CCW
    // (interior on the LEFT). For CW reversed.
    const perpInX = ccw ? dirInY : -dirInY;
    const perpInY = ccw ? -dirInX : dirInX;
    const perpOutX = ccw ? dirOutY : -dirOutY;
    const perpOutY = ccw ? -dirOutX : dirOutX;

    const touchInX = here.x + perpInX * offset;
    const touchInY = here.y + perpInY * offset;
    const touchOutX = here.x + perpOutX * offset;
    const touchOutY = here.y + perpOutY * offset;

    const a0 = Math.atan2(touchInY - here.y, touchInX - here.x);
    const a1 = Math.atan2(touchOutY - here.y, touchOutX - here.x);

    let delta = a1 - a0;
    if (ccw) {
      while (delta < 0) delta += 2 * Math.PI;
    } else {
      while (delta > 0) delta -= 2 * Math.PI;
    }
    if (Math.abs(delta) > Math.PI) {
      delta -= Math.sign(delta) * 2 * Math.PI;
    }

    const segs = Math.max(arcSegmentsPerCorner, 2);
    for (let s = 0; s <= segs; s++) {
      const t = s / segs;
      const angle = a0 + delta * t;
      output.push(
        new THREE.Vector3(
          here.x + Math.cos(angle) * offset,
          here.y + Math.sin(angle) * offset,
          0,
        ),
      );
    }
  }
}

/**
 * Distance from a 2D point to the closest segment of the band's closed
 * loop polyline. Treats `loopPoints` as a CLOSED polyline (the last point
 * connects back to the first).
 */
export function distanceToLoop(loopPoints: THREE.Vector3[], px: number, py: number): number {
  const n = loopPoints.length;
  if (n === 0) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < n; i++) {
    const a = loopPoints[i];
    const b = loopPoints[(i + 1) % n];
    const d = distancePointToSegment(px, py, a.x, a.y, b.x, b.y);
    if (d < best) best = d;
  }
  return best;
}

function distancePointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const sq = Math.max(1e-6, abx * abx + aby * aby);
  let t = ((px - ax) * abx + (py - ay) * aby) / sq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = ax + abx * t;
  const cy = ay + aby * t;
  const dx = px - cx;
  const dy = py - cy;
  return Math.hypot(dx, dy);
}
